import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Trash2, Plus, X, Upload as UploadIcon } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STATUS = ["pending", "confirmed", "completed", "rejected"];

function ImageUploader({ value, onChange, namespace = "services", testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen válida (JPG, PNG o WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen debe pesar menos de 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/upload?namespace=${namespace}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          className="field flex-1"
          placeholder="Pega una URL o sube una imagen →"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid={testid ? `${testid}-file` : undefined} />
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="btn-outline flex items-center gap-2 disabled:opacity-50"
          data-testid={testid ? `${testid}-upload` : undefined}
        >
          <UploadIcon size={14} /> {uploading ? "Subiendo…" : "Subir"}
        </button>
      </div>
      {value && (
        <div className="zoom-frame aspect-[16/9] border border-[#E8E1DA]">
          <img src={value} alt="preview" />
        </div>
      )}
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    pending: "bg-[#F4F0EC] text-[#1A1A1A]",
    confirmed: "bg-[#3C6348] text-white",
    completed: "bg-[#1A1A1A] text-white",
    rejected: "bg-[#9E3C3C] text-white",
  };
  return <span className={`overline px-3 py-1 ${map[status] || "bg-[#F4F0EC]"}`}>{status}</span>;
}

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("studio-pass");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] overline text-[#5C5C5C]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <header className="border-b border-[#E8E1DA] sticky top-0 bg-[#FAFAFA] z-20">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl">
            Abril Suarez<span className="text-[#8B6D5E]">.</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/qr" className="overline hover:text-[#8B6D5E] hidden sm:inline" data-testid="admin-qr-link">QR Tarjetas</Link>
            <p className="overline text-[#5C5C5C] hidden sm:block">{user.email}</p>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="overline flex items-center gap-2 hover:text-[#8B6D5E]" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
        <h1 className="font-serif text-5xl tracking-tighter mb-2">Panel</h1>
        <p className="overline text-[#5C5C5C] mb-10 hidden sm:block">Studio Pass · Alumnas · Reservas · Quiz Leads · Servicios · Cursos · Galería</p>

        <div className="-mx-6 sm:mx-0 mb-8 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          <div className="flex gap-2 px-6 sm:px-0 border-b border-[#E8E1DA] min-w-max sm:min-w-0">
            {[
              ["studio-pass", "Studio Pass"],
              ["students", "Alumnas"],
              ["bookings", "Reservas"],
              ["quiz", "Quiz"],
              ["services", "Servicios"],
              ["courses", "Cursos"],
              ["gallery", "Galería"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`tab-${k}`}
                className={`whitespace-nowrap text-[13px] tracking-[0.15em] uppercase px-4 py-3 border-b-2 transition-colors ${
                  tab === k
                    ? "border-[#1A1A1A] text-[#1A1A1A] font-medium"
                    : "border-transparent text-[#8B6D5E] hover:text-[#1A1A1A]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "studio-pass" && <StudioPassPanel />}
        {tab === "students" && <StudentsPanel />}
        {tab === "bookings" && <BookingsPanel />}
        {tab === "quiz" && <QuizLeadsPanel />}
        {tab === "services" && <ServicesPanel />}
        {tab === "courses" && <CoursesPanel />}
        {tab === "gallery" && <GalleryPanel />}
      </div>
    </div>
  );
}

function BookingsPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => (filter === "all" ? items : items.filter((b) => b.status === filter)), [items, filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setItems((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta reserva?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setItems((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="bookings-panel">
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar" data-testid="booking-filters">
        {["all", ...STATUS].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`whitespace-nowrap text-[12px] tracking-[0.15em] uppercase px-4 py-2 rounded-full border transition-colors ${filter === s ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E8E1DA] text-[#5C5C5C] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"}`}
          >
            {s === "all" ? "Todas" : s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="overline text-[#5C5C5C] py-10" data-testid="bookings-empty">Sin reservas</p>
      ) : (
        <div className="border border-[#E8E1DA] divide-y divide-[#E8E1DA]">
          {filtered.map((b) => (
            <div key={b.id} className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-start" data-testid={`booking-${b.id}`}>
              <div className="lg:col-span-3">
                <p className="font-serif text-2xl">{b.name}</p>
                <p className="text-sm text-[#5C5C5C]">{b.email}</p>
                <p className="text-sm text-[#5C5C5C]">{b.phone}</p>
              </div>
              <div className="lg:col-span-3 text-sm">
                <p><span className="overline text-[#8B6D5E]">Servicio: </span>{b.service_type}</p>
                {b.event_date && <p><span className="overline text-[#8B6D5E]">Fecha: </span>{b.event_date}</p>}
                {b.location && <p><span className="overline text-[#8B6D5E]">Lugar: </span>{b.location}</p>}
              </div>
              <div className="lg:col-span-3 text-sm text-[#5C5C5C]">
                {b.message && <p className="line-clamp-4">{b.message}</p>}
                <p className="overline text-[#5C5C5C] mt-2">{new Date(b.created_at).toLocaleString()}</p>
              </div>
              <div className="lg:col-span-3 flex flex-col gap-2">
                <div><StatusBadge status={b.status} /></div>
                <select
                  value={b.status}
                  onChange={(e) => updateStatus(b.id, e.target.value)}
                  className="border border-[#E8E1DA] px-3 py-2 text-sm bg-white"
                  data-testid={`booking-status-${b.id}`}
                >
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(b.id)} className="overline text-[#9E3C3C] flex items-center gap-2" data-testid={`booking-delete-${b.id}`}>
                  <Trash2 size={14} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const blankCourse = {
  slug: "",
  title_es: "", title_en: "", title_jp: "",
  description_es: "", description_en: "", description_jp: "",
  duration: "", price: 0, image_url: "", next_date: "", spots: 8, order: 0,
};

const EVENT_LABELS = {
  "boda": "Boda",
  "xv-anos": "XV Años",
  "graduacion": "Graduación",
  "cena": "Cena Nocturna",
  "invitada-diurno": "Invitada (diurno)",
  "invitada-comida": "Invitada (comida)",
  "concurso-belleza": "Concurso de Belleza",
  "sesion-foto": "Sesión Fotográfica",
  "otro": "Otro",
};

function QuizLeadsPanel() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/quiz-leads"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar lead?")) return;
    try { await api.delete(`/admin/quiz-leads/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="quiz-leads-panel">
      {items.length === 0 ? (
        <p className="overline text-[#5C5C5C] py-10" data-testid="quiz-leads-empty">Sin leads aún</p>
      ) : (
        <div className="border border-[#E8E1DA] divide-y divide-[#E8E1DA]">
          {items.map((l) => (
            <div key={l.id} className="p-6" data-testid={`quiz-lead-${l.id}`}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-3">
                  <p className="font-serif text-2xl">{l.name}</p>
                  <p className="text-sm text-[#5C5C5C]">{l.email}</p>
                  <p className="text-sm text-[#5C5C5C]">{l.phone}</p>
                </div>
                <div className="lg:col-span-3 text-sm">
                  <p><span className="overline text-[#8B6D5E]">Evento: </span>{EVENT_LABELS[l.event_type] || l.event_type}</p>
                  <p><span className="overline text-[#8B6D5E]">Código: </span><span className="font-serif">{l.discount_code}</span></p>
                  <p>
                    <span className="overline text-[#8B6D5E]">Agenda ahora: </span>
                    <span className={l.schedule_now ? "text-[#3C6348] font-medium" : "text-[#9E3C3C]"}>{l.schedule_now ? "Sí" : "No"}</span>
                  </p>
                </div>
                <div className="lg:col-span-4 text-sm text-[#5C5C5C]">
                  <p className="overline text-[#5C5C5C]">{new Date(l.created_at).toLocaleString()}</p>
                  {l.photos?.length > 0 && (
                    <p className="text-xs mt-2">📷 {l.photos.length} foto(s) adjuntas</p>
                  )}
                </div>
                <div className="lg:col-span-2 flex flex-col gap-2 items-start lg:items-end">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/quiz/${l.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="overline link-underline text-[#3C6348]"
                    data-testid={`download-pdf-${l.id}`}
                  >
                    Descargar PDF
                  </a>
                  <button onClick={() => setOpenId(openId === l.id ? null : l.id)} className="overline link-underline" data-testid={`view-lead-${l.id}`}>
                    {openId === l.id ? "Ocultar" : "Ver respuestas"}
                  </button>
                  <button onClick={() => remove(l.id)} className="overline text-[#9E3C3C]" data-testid={`delete-lead-${l.id}`}>Eliminar</button>
                </div>
              </div>
              {openId === l.id && (
                <div className="mt-6 border-t border-[#E8E1DA] pt-6">
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    {Object.entries(l.answers || {}).map(([k, v]) => (
                      <div key={k}>
                        <p className="overline text-[#8B6D5E] mb-1">{k}</p>
                        <p>{String(v)}</p>
                      </div>
                    ))}
                  </div>
                  {l.photos?.length > 0 && (
                    <div className="mt-6 grid grid-cols-3 gap-4 max-w-2xl">
                      {l.photos.map((src, i) => (
                        <div key={i} className="zoom-frame aspect-square">
                          <img src={src} alt={`Inspiración ${i + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const blankService = {
  slug: "",
  title_es: "", title_en: "", title_jp: "",
  description_es: "", description_en: "", description_jp: "",
  price_from: 0, image_url: "", order: 0,
};

function ServicesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/services"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      const payload = { ...editing, price_from: Number(editing.price_from), order: Number(editing.order) };
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar servicio?")) return;
    try { await api.delete(`/admin/services/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="services-panel">
      <div className="flex justify-end mb-6">
        <button onClick={() => setEditing({ ...blankService })} className="btn-primary" data-testid="add-service">
          <Plus size={14} /> Nuevo servicio
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((s) => (
          <div key={s.id} className="border border-[#E8E1DA] p-5 bg-white flex flex-col" data-testid={`admin-service-${s.slug}`}>
            <div className="zoom-frame aspect-[4/3] mb-3"><img src={s.image_url} alt={s.title_es} /></div>
            <p className="overline text-[#8B6D5E]">{s.slug}</p>
            <h3 className="font-serif text-xl mt-1">{s.title_es}</h3>
            <p className="text-sm text-[#5C5C5C] mt-1 line-clamp-2">{s.description_es}</p>
            <p className="overline text-[#1A1A1A] mt-3" data-testid={`service-price-${s.slug}`}>
              Desde ${s.price_from.toLocaleString("es-MX")} MXN
            </p>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditing(s)} className="overline link-underline" data-testid={`edit-service-${s.slug}`}>Editar</button>
              <button onClick={() => remove(s.id)} className="overline text-[#9E3C3C]" data-testid={`delete-service-${s.slug}`}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditDrawer title={editing.id ? "Editar servicio" : "Nuevo servicio"} onClose={() => setEditing(null)} onSave={save}>
          <Field label="Slug"><input className="field" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="service-edit-slug" /></Field>
          <Field label="Imagen">
            <ImageUploader value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} namespace="services" testid="service-edit-image" />
          </Field>
          <Field label="Título ES"><input className="field" value={editing.title_es} onChange={(e) => setEditing({ ...editing, title_es: e.target.value })} data-testid="service-edit-title-es" /></Field>
          <Field label="Título EN"><input className="field" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></Field>
          <Field label="Título JP"><input className="field" value={editing.title_jp} onChange={(e) => setEditing({ ...editing, title_jp: e.target.value })} /></Field>
          <Field label="Descripción ES"><textarea className="field" value={editing.description_es} onChange={(e) => setEditing({ ...editing, description_es: e.target.value })} data-testid="service-edit-desc-es" /></Field>
          <Field label="Descripción EN"><textarea className="field" value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} /></Field>
          <Field label="Descripción JP"><textarea className="field" value={editing.description_jp} onChange={(e) => setEditing({ ...editing, description_jp: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Precio desde (MXN)"><input type="number" className="field" value={editing.price_from} onChange={(e) => setEditing({ ...editing, price_from: e.target.value })} data-testid="service-edit-price" /></Field>
            <Field label="Orden"><input type="number" className="field" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></Field>
          </div>
        </EditDrawer>
      )}
    </div>
  );
}

function CoursesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/courses"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      const payload = { ...editing, price: Number(editing.price), spots: Number(editing.spots), order: Number(editing.order) };
      if (editing.id) await api.put(`/admin/courses/${editing.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar curso?")) return;
    try { await api.delete(`/admin/courses/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="courses-panel">
      <div className="flex justify-end mb-6">
        <button onClick={() => setEditing({ ...blankCourse })} className="btn-primary" data-testid="add-course">
          <Plus size={14} /> Nuevo curso
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <div key={c.id} className="border border-[#E8E1DA] p-5 bg-white" data-testid={`admin-course-${c.slug}`}>
            <div className="zoom-frame aspect-[4/3] mb-3"><img src={c.image_url} alt={c.title_es} /></div>
            <p className="overline text-[#8B6D5E]">{c.slug}</p>
            <h3 className="font-serif text-xl mt-1">{c.title_es}</h3>
            <p className="text-sm text-[#5C5C5C] mt-1">${c.price.toLocaleString("es-MX")} · {c.duration}</p>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditing(c)} className="overline link-underline" data-testid={`edit-course-${c.slug}`}>Editar</button>
              <button onClick={() => remove(c.id)} className="overline text-[#9E3C3C]" data-testid={`delete-course-${c.slug}`}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditDrawer title={editing.id ? "Editar curso" : "Nuevo curso"} onClose={() => setEditing(null)} onSave={save}>
          <Field label="Slug"><input className="field" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></Field>
          <Field label="Imagen">
            <ImageUploader value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} namespace="courses" testid="course-edit-image" />
          </Field>
          <Field label="Título ES"><input className="field" value={editing.title_es} onChange={(e) => setEditing({ ...editing, title_es: e.target.value })} /></Field>
          <Field label="Título EN"><input className="field" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></Field>
          <Field label="Título JP"><input className="field" value={editing.title_jp} onChange={(e) => setEditing({ ...editing, title_jp: e.target.value })} /></Field>
          <Field label="Descripción ES"><textarea className="field" value={editing.description_es} onChange={(e) => setEditing({ ...editing, description_es: e.target.value })} /></Field>
          <Field label="Descripción EN"><textarea className="field" value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} /></Field>
          <Field label="Descripción JP"><textarea className="field" value={editing.description_jp} onChange={(e) => setEditing({ ...editing, description_jp: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Duración"><input className="field" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} /></Field>
            <Field label="Precio (MXN)"><input type="number" className="field" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></Field>
            <Field label="Próxima fecha"><input type="date" className="field" value={editing.next_date || ""} onChange={(e) => setEditing({ ...editing, next_date: e.target.value })} /></Field>
            <Field label="Cupos"><input type="number" className="field" value={editing.spots} onChange={(e) => setEditing({ ...editing, spots: e.target.value })} /></Field>
          </div>
        </EditDrawer>
      )}
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", category: "novias", title: "", order: 0 });

  const load = async () => {
    try { const { data } = await api.get("/gallery"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/gallery", { ...form, order: Number(form.order) });
      toast.success("Agregado");
      setForm({ image_url: "", category: "novias", title: "", order: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/admin/gallery/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="gallery-panel">
      <form onSubmit={add} className="border border-[#E8E1DA] p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white" data-testid="add-gallery-form">
        <div className="md:col-span-2">
          <Field label="Imagen (URL o subir)">
            <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} namespace="gallery" testid="gallery-image-url" />
          </Field>
        </div>
        <Field label="Categoría">
          <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="gallery-category-select">
            <option value="novias">Novias</option>
            <option value="sociales">Sociales</option>
            <option value="clases">Clases</option>
            <option value="servicio">Nuestro Servicio</option>
          </select>
        </Field>
        <Field label="Título"><input className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label=" "><button type="submit" className="btn-primary w-full justify-center" data-testid="add-gallery-submit"><Plus size={14}/> Agregar a galería</button></Field>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.id} className="border border-[#E8E1DA] bg-white relative group" data-testid={`gallery-${it.id}`}>
            <div className="zoom-frame aspect-square"><img src={it.image_url} alt={it.title} /></div>
            <div className="p-3 flex items-center justify-between">
              <div>
                <p className="overline text-[#8B6D5E]">{it.category}</p>
                {it.title && <p className="text-sm">{it.title}</p>}
              </div>
              <button onClick={() => remove(it.id)} className="text-[#9E3C3C]" data-testid={`gallery-delete-${it.id}`} aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function EditDrawer({ title, children, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="edit-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#FAFAFA] w-full sm:w-[560px] h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#FAFAFA] border-b border-[#E8E1DA] px-8 py-5 flex items-center justify-between z-10">
          <h2 className="font-serif text-2xl">{title}</h2>
          <button onClick={onClose} aria-label="Close" data-testid="drawer-close"><X size={22} /></button>
        </div>
        <div className="p-8 space-y-6">{children}</div>
        <div className="sticky bottom-0 bg-[#FAFAFA] border-t border-[#E8E1DA] px-8 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline" data-testid="drawer-cancel">Cancelar</button>
          <button onClick={onSave} className="btn-primary" data-testid="drawer-save">Guardar</button>
        </div>
      </div>
    </div>
  );
}


// ─── STUDIO PASS PANEL ──────────────────────────────────────────────────────
function StudioPassPanel() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [{ data: appts }, { data: svc }] = await Promise.all([
        api.get("/admin/appointments"),
        api.get("/services"),
      ]);
      setItems(appts);
      setServices(svc);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const emptyForm = () => ({
    client_name: "",
    client_email: "",
    client_phone: "",
    service_slug: services[0]?.slug || "",
    date: new Date().toISOString().slice(0, 10),
    start_time: "09:00",
    duration_minutes: 90,
    location_address: "",
    location_map_url: "",
    location_notes: "",
    location_type: "studio",
    payment_status: "paid",
    amount_total: "",
    amount_paid: "",
    abril_notes: "",
    client_notes: "",
    reference_images: [],
    checklist: [],
  });

  const [form, setForm] = useState(emptyForm());
  useEffect(() => {
    if (open && !editing) setForm(emptyForm());
    if (open && editing) setForm({ ...emptyForm(), ...editing });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, services.length]);

  const save = async () => {
    try {
      const payload = { ...form };
      payload.amount_total = payload.amount_total === "" ? null : Number(payload.amount_total);
      payload.amount_paid = payload.amount_paid === "" ? null : Number(payload.amount_paid);
      payload.duration_minutes = Number(payload.duration_minutes) || 90;
      if (editing?.id) {
        await api.put(`/admin/appointments/${editing.id}`, payload);
        toast.success("Cita actualizada");
      } else {
        await api.post("/admin/appointments", payload);
        toast.success("Cita creada · Studio Pass enviado");
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cita? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      toast.success("Cita eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/appointments/${id}/resend`, {});
      toast.success("Studio Pass reenviado (WhatsApp + email)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendMedia = async (id) => {
    try {
      await api.post(`/admin/appointments/${id}/send-media`, {});
      toast.success("QR + PDF enviados por WhatsApp");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/appointments/${id}/status`, { status });
      toast.success(`Estado: ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="overline text-[#5C5C5C]" data-testid="pass-count">{items.length} cita{items.length === 1 ? "" : "s"}</p>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary" data-testid="new-appointment-btn">
          <Plus size={16} className="inline mr-2" /> Nueva cita
        </button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="border border-[#E8E1DA] p-10 text-center">
            <p className="overline text-[#8B6D5E] mb-2">Sin citas todavía</p>
            <p className="text-sm text-[#5C5C5C]">Crea la primera cita para generar tu primer Studio Pass.</p>
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="border border-[#E8E1DA] p-5 grid lg:grid-cols-[1fr_auto_auto] gap-4 items-start" data-testid={`appointment-${a.id}`}>
            <div>
              <p className="font-serif text-xl">{a.client_name} <span className="text-[#8B6D5E]">— {a.service_title}</span></p>
              <p className="overline text-[#5C5C5C] mt-1">{a.date} · {a.start_time}</p>
              <p className="text-sm text-[#5C5C5C]">{a.client_phone} · {a.client_email || "sin email"}</p>
              {a.location_address && <p className="text-xs text-[#5C5C5C] mt-1">{a.location_address}</p>}
            </div>
            <div className="flex flex-col gap-1 items-start lg:items-end">
              <span className={`overline px-3 py-1 ${
                a.status === "confirmed" ? "bg-[#3C6348] text-white" :
                a.status === "checked_in" ? "bg-[#1A1A1A] text-white" :
                a.status === "completed" ? "bg-[#F4F0EC] text-[#1A1A1A]" :
                "bg-[#9E3C3C] text-white"
              }`}>{a.status}</span>
              <span className="overline text-[#8B6D5E]">Pago: {a.payment_status}</span>
            </div>
            <div className="flex flex-col gap-1 items-start lg:items-end text-right">
              <a href={a.portal_url} target="_blank" rel="noopener noreferrer" className="overline link-underline text-[#3C6348]" data-testid={`open-pass-${a.id}`}>Ver Studio Pass</a>
              <a href={`${backend}/api/tickets/${a.token}/pdf`} target="_blank" rel="noopener noreferrer" className="overline link-underline">Descargar PDF</a>
              <a href={`${backend}/api/tickets/${a.token}/qr.png`} target="_blank" rel="noopener noreferrer" className="overline link-underline">Ver QR</a>
              <button onClick={() => resend(a.id)} className="overline link-underline text-[#8B6D5E]" data-testid={`resend-${a.id}`}>Reenviar</button>
              <button onClick={() => sendMedia(a.id)} className="overline link-underline text-[#8B6D5E]" data-testid={`send-media-${a.id}`}>Enviar QR + PDF</button>
              <a
                href={`https://wa.me/${(a.client_phone || "").replace(/\D/g, "").replace(/^521/, "52").replace(/^(?!52)/, "52")}?text=${encodeURIComponent(
                  `Hola ${(a.client_name || "").split(" ")[0]}! Aquí tu Studio Pass para tu ${a.service_title} del ${a.date} a las ${a.start_time}.\n\n` +
                  `🎫 Tu portal: ${a.portal_url}\n` +
                  `📄 PDF: ${process.env.REACT_APP_BACKEND_URL}/api/tickets/${a.token}/pdf\n` +
                  `📱 QR (guárdalo como imagen): ${process.env.REACT_APP_BACKEND_URL}/api/tickets/${a.token}/qr.png\n\n` +
                  `Cualquier duda, aquí estoy. — Abril ✨`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="overline link-underline text-[#3C6348]"
                data-testid={`wa-manual-${a.id}`}
              >
                WhatsApp manual
              </a>
              <button onClick={() => { setEditing(a); setOpen(true); }} className="overline link-underline">Editar</button>
              {a.status !== "checked_in" && a.status !== "completed" && (
                <button onClick={() => setStatus(a.id, "checked_in")} className="overline link-underline text-[#3C6348]">Check-in</button>
              )}
              {a.status !== "completed" && (
                <button onClick={() => setStatus(a.id, "completed")} className="overline link-underline">Marcar terminada</button>
              )}
              <button onClick={() => remove(a.id)} className="overline text-[#9E3C3C]" data-testid={`delete-appt-${a.id}`}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <EditDrawer onClose={() => { setOpen(false); setEditing(null); }} onSave={save} title={editing ? "Editar cita" : "Nueva cita"}>
        <div className="grid grid-cols-2 gap-4">
          <ApptField label="Nombre cliente" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="appt-name" />
          <ApptField label="Teléfono (con o sin +52)" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} testid="appt-phone" />
          <ApptField label="Email (opcional)" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} />
          <div>
            <label className="overline block mb-2">Servicio</label>
            <select
              value={form.service_slug}
              onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
              className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
              data-testid="appt-service"
            >
              {services.map((s) => <option key={s.slug} value={s.slug}>{s.title_es}</option>)}
            </select>
          </div>
          <ApptField label="Fecha" value={form.date} onChange={(v) => setForm({ ...form, date: v })} type="date" testid="appt-date" />
          <ApptField label="Hora inicio" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} type="time" testid="appt-time" />
          <ApptField label="Duración (minutos)" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: v })} type="number" />
          <div>
            <label className="overline block mb-2">Estado de pago</label>
            <select
              value={form.payment_status}
              onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
              className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
            >
              <option value="paid">Pagado</option>
              <option value="deposit">Anticipo</option>
              <option value="pending">Pendiente</option>
            </select>
          </div>
          <ApptField label="Total (MXN)" value={form.amount_total} onChange={(v) => setForm({ ...form, amount_total: v })} type="number" />
          <ApptField label="Pagado (MXN)" value={form.amount_paid} onChange={(v) => setForm({ ...form, amount_paid: v })} type="number" />
        </div>
        <div>
          <label className="overline block mb-2">Tipo de ubicación</label>
          <select
            value={form.location_type || "studio"}
            onChange={(e) => setForm({ ...form, location_type: e.target.value })}
            className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
            data-testid="appt-location-type"
          >
            <option value="studio">Studio (viene a mí)</option>
            <option value="domicilio">Domicilio (voy yo)</option>
            <option value="venue">Venue / Evento</option>
          </select>
        </div>
        <ApptField label="Dirección" value={form.location_address} onChange={(v) => setForm({ ...form, location_address: v })} />
        <ApptField label="Link Google Maps (opcional)" value={form.location_map_url} onChange={(v) => setForm({ ...form, location_map_url: v })} />
        <ApptField label="Notas de ubicación" value={form.location_notes} onChange={(v) => setForm({ ...form, location_notes: v })} textarea />
        <ApptField label="Mensaje personal para la clienta" value={form.client_notes} onChange={(v) => setForm({ ...form, client_notes: v })} textarea />
        <ApptField label="Notas privadas (solo Abril)" value={form.abril_notes} onChange={(v) => setForm({ ...form, abril_notes: v })} textarea />
        <p className="overline text-[#5C5C5C]">La checklist previa se genera automáticamente según el servicio elegido.</p>
        </EditDrawer>
      )}
    </div>
  );
}

function ApptField({ label, value, onChange, type = "text", textarea = false, testid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
          data-testid={testid}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Academia · Alumnas
// ─────────────────────────────────────────
function StudentsPanel() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", course_slug: "", notes: "" });
  const [sending, setSending] = useState(null);
  const [passwords, setPasswords] = useState({});
  const [unitsMeta, setUnitsMeta] = useState([]);
  const [pwDirty, setPwDirty] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwShow, setPwShow] = useState(false);
  const [sendPwFor, setSendPwFor] = useState(null); // student
  const [sendPwUnits, setSendPwUnits] = useState(new Set());
  const [sendPwLoading, setSendPwLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/students");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadPasswords = async () => {
    try {
      const { data } = await api.get("/admin/unit-passwords");
      setPasswords(data.passwords || {});
      setUnitsMeta(data.units || []);
      setPwDirty(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    loadPasswords();
  }, []);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Necesito al menos el nombre de la alumna.");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        course_slug: form.course_slug || undefined,
        notes: form.notes.trim() || undefined,
      };
      const { data } = await api.post("/admin/students", payload);
      setItems([data, ...items]);
      setCreating(false);
      setForm({ name: "", email: "", phone: "", course_slug: "", notes: "" });
      toast.success(`Acceso creado para ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      const { data } = await api.put(`/admin/students/${editing.id}`, {
        name: editing.name,
        email: editing.email || undefined,
        phone: editing.phone || undefined,
        course_slug: editing.course_slug || undefined,
        notes: editing.notes || undefined,
        status: editing.status,
      });
      setItems(items.map((it) => (it.id === data.id ? data : it)));
      setEditing(null);
      toast.success("Alumna actualizada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (student) => {
    if (!window.confirm(`¿Eliminar acceso de ${student.name}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/students/${student.id}`);
      setItems(items.filter((it) => it.id !== student.id));
      toast.success("Alumna eliminada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resend = async (student) => {
    setSending(student.id);
    try {
      await api.post(`/admin/students/${student.id}/resend`);
      toast.success(`Link enviado a ${student.name.split(" ")[0]}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(null);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const updatePw = (unitNum, value) => {
    const clean = (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
    setPasswords((prev) => ({ ...prev, [String(unitNum)]: clean }));
    setPwDirty(true);
  };

  const savePasswords = async () => {
    for (const u of unitsMeta) {
      if (!passwords[String(u.number)] || passwords[String(u.number)].trim().length < 3) {
        toast.error(`Contraseña muy corta para la unidad ${u.number}`);
        return;
      }
    }
    setPwSaving(true);
    try {
      await api.put("/admin/unit-passwords", { passwords });
      toast.success("Contraseñas guardadas");
      setPwDirty(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const openSendPasswords = (student) => {
    if (!student.email) {
      toast.error("La alumna no tiene email registrado. Edítala primero.");
      return;
    }
    setSendPwFor(student);
    setSendPwUnits(new Set());
  };

  const toggleSendUnit = (n) => {
    setSendPwUnits((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const sendPasswords = async () => {
    if (!sendPwFor || sendPwUnits.size === 0) {
      toast.error("Selecciona al menos una unidad");
      return;
    }
    setSendPwLoading(true);
    try {
      const unit_numbers = Array.from(sendPwUnits).sort((a, b) => a - b);
      await api.post(`/admin/students/${sendPwFor.id}/send-unit-passwords`, {
        unit_numbers,
        include_link: true,
      });
      toast.success(`Contraseñas enviadas a ${sendPwFor.name.split(" ")[0]}`);
      setSendPwFor(null);
      setSendPwUnits(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendPwLoading(false);
    }
  };

  const resetUnlocks = async (student) => {
    const count = (student.unlocked_units || []).length;
    if (count === 0) {
      toast("Esta alumna no tiene candados abiertos.", { duration: 1800 });
      return;
    }
    if (!window.confirm(`¿Reiniciar candados de ${student.name}? Se bloquearán las ${count} unidad(es) que había abierto.`)) return;
    try {
      const { data } = await api.post(`/admin/students/${student.id}/reset-unlocks`);
      setItems((prev) =>
        prev.map((it) => (it.id === student.id ? { ...it, unlocked_units: data.unlocked_units } : it))
      );
      toast.success("Candados reiniciados");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="students-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl">Alumnas · Academia</h2>
          <p className="overline text-[#5C5C5C] mt-1">Gestiona los accesos al manual del método</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="btn-solid flex items-center gap-2"
          data-testid="student-create-btn"
        >
          <Plus size={14} /> Nueva alumna
        </button>
      </div>

      {/* Unit passwords manager */}
      {unitsMeta.length > 0 && (
        <div className="border border-[#E8E1DA] mb-10" data-testid="unit-passwords-panel">
          <button
            onClick={() => setPwShow((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#F7F0E8]/40 transition-colors"
            data-testid="unit-passwords-toggle"
          >
            <div className="text-left">
              <p className="overline text-[#8B6D5E]">— Contraseñas por unidad</p>
              <p className="font-serif text-lg mt-1">
                Contraseñas globales · {unitsMeta.length} unidades
              </p>
            </div>
            <span className="overline text-[#5C5C5C]">
              {pwShow ? "Ocultar ▲" : "Mostrar ▼"}
            </span>
          </button>
          {pwShow && (
            <div className="border-t border-[#E8E1DA] p-5 bg-[#FAFAFA]">
              <p className="text-sm text-[#5C5C5C] mb-4 max-w-2xl">
                Cada unidad se abre con la contraseña que definas aquí. Son las mismas para todas
                las alumnas. Cámbialas cuando quieras — la próxima vez que una alumna intente
                desbloquear, deberá usar la nueva.
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {unitsMeta.map((u) => (
                  <label
                    key={u.number}
                    className="flex items-center gap-3 group"
                    data-testid={`unit-pw-row-${u.number}`}
                  >
                    <span className="overline text-[#8B6D5E] w-8 flex-none">
                      {String(u.number).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-[#1A1A1A] truncate">
                      {u.title}
                    </span>
                    <input
                      type="text"
                      value={passwords[String(u.number)] || ""}
                      onChange={(e) => updatePw(u.number, e.target.value)}
                      autoCapitalize="characters"
                      spellCheck={false}
                      className="w-32 border border-[#E8E1DA] focus:border-[#1A1A1A] outline-none px-3 py-1.5 font-mono tracking-[0.15em] text-sm text-[#1A1A1A] bg-white"
                      data-testid={`unit-pw-input-${u.number}`}
                    />
                  </label>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-end gap-3">
                {pwDirty && <span className="overline text-[#8B6D5E]">— Sin guardar</span>}
                <button
                  onClick={savePasswords}
                  disabled={!pwDirty || pwSaving}
                  className="btn-solid text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="unit-pw-save"
                >
                  {pwSaving ? "Guardando…" : "Guardar contraseñas"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-[#E8E1DA] p-14 text-center bg-[#F7F0E8]/40">
          <p className="overline text-[#8B6D5E] mb-3">— Sin alumnas todavía</p>
          <p className="font-serif text-2xl mb-2">Crea el primer acceso.</p>
          <p className="text-[#5C5C5C] text-sm">Cada alumna recibe un link personal y un PDF con su nombre.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="students-list">
          {items.map((s) => (
            <div key={s.id} className="border border-[#E8E1DA] p-5 hover:border-[#8B6D5E] transition-colors" data-testid={`student-row-${s.id}`}>
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-serif text-xl leading-tight">{s.name}</h3>
                    <span className={`overline px-2 py-0.5 text-[10px] ${s.status === "archived" ? "bg-[#F4F0EC] text-[#5C5C5C]" : "bg-[#3C6348] text-white"}`}>
                      {s.status === "archived" ? "Pausado" : "Activo"}
                    </span>
                    <span
                      className="overline text-[10px] text-[#8B6D5E]"
                      data-testid={`student-unlocked-count-${s.id}`}
                      title="Unidades desbloqueadas por la alumna"
                    >
                      {(s.unlocked_units || []).length} / 10 unidades abiertas
                    </span>
                  </div>
                  <div className="text-sm text-[#5C5C5C] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    {s.email && <span>· {s.email}</span>}
                    {s.phone && <span>· {s.phone}</span>}
                    {s.course_slug && <span>· {s.course_slug}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <code className="text-xs bg-[#F7F0E8] px-2 py-1 truncate max-w-full">{s.manual_url}</code>
                    <button
                      onClick={() => copyLink(s.manual_url)}
                      className="overline text-[#8B6D5E] hover:text-[#1A1A1A] text-[10px]"
                      data-testid={`student-copy-link-${s.id}`}
                    >
                      Copiar link
                    </button>
                    <a
                      href={s.manual_url}
                      target="_blank"
                      rel="noreferrer"
                      className="overline text-[#8B6D5E] hover:text-[#1A1A1A] text-[10px]"
                      data-testid={`student-open-manual-${s.id}`}
                    >
                      Abrir manual ↗
                    </a>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 md:justify-end">
                  <button
                    onClick={() => openSendPasswords(s)}
                    className="btn-outline text-[11px]"
                    data-testid={`student-send-pw-${s.id}`}
                    title="Enviar contraseñas por email"
                  >
                    Contraseñas
                  </button>
                  <button
                    onClick={() => resetUnlocks(s)}
                    className="btn-outline text-[11px]"
                    data-testid={`student-reset-unlocks-${s.id}`}
                    title="Reiniciar candados — bloquea todas las unidades otra vez"
                  >
                    Reiniciar
                  </button>
                  <button
                    onClick={() => resend(s)}
                    disabled={sending === s.id}
                    className="btn-outline text-[11px] disabled:opacity-50"
                    data-testid={`student-resend-${s.id}`}
                  >
                    {sending === s.id ? "Enviando…" : "Enviar link"}
                  </button>
                  <button
                    onClick={() => setEditing({ ...s })}
                    className="btn-outline text-[11px]"
                    data-testid={`student-edit-${s.id}`}
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => remove(s)}
                    className="btn-outline text-[11px] text-[#9E3C3C] border-[#9E3C3C] hover:bg-[#9E3C3C] hover:text-white"
                    data-testid={`student-delete-${s.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              {s.notes && (
                <p className="text-sm text-[#5C5C5C] mt-3 pt-3 border-t border-[#E8E1DA] italic">
                  {s.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <EditDrawer title="Nueva alumna" onClose={() => setCreating(false)} onSave={create}>
          <ApptField label="Nombre completo" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="student-form-name" />
          <ApptField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="student-form-email" />
          <ApptField label="Teléfono (MX)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="student-form-phone" />
          <ApptField label="Curso / grupo (opcional)" value={form.course_slug} onChange={(v) => setForm({ ...form, course_slug: v })} testid="student-form-course" />
          <ApptField label="Notas privadas" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} textarea testid="student-form-notes" />
          <p className="overline text-[#5C5C5C]">Al guardar se genera el link y el PDF personalizado. Usa "Enviar link" para mandárselo por email + WhatsApp.</p>
        </EditDrawer>
      )}

      {editing && (
        <EditDrawer title={`Editar · ${editing.name}`} onClose={() => setEditing(null)} onSave={save}>
          <ApptField label="Nombre completo" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
          <ApptField label="Email" value={editing.email || ""} onChange={(v) => setEditing({ ...editing, email: v })} />
          <ApptField label="Teléfono (MX)" value={editing.phone || ""} onChange={(v) => setEditing({ ...editing, phone: v })} />
          <ApptField label="Curso / grupo" value={editing.course_slug || ""} onChange={(v) => setEditing({ ...editing, course_slug: v })} />
          <ApptField label="Notas privadas" value={editing.notes || ""} onChange={(v) => setEditing({ ...editing, notes: v })} textarea />
          <div>
            <label className="overline block mb-2">Estado</label>
            <select
              value={editing.status || "active"}
              onChange={(e) => setEditing({ ...editing, status: e.target.value })}
              className="w-full border border-[#E8E1DA] px-3 py-2 bg-[#FAFAFA]"
              data-testid="student-form-status"
            >
              <option value="active">Activo (puede abrir manual)</option>
              <option value="archived">Pausado (acceso bloqueado)</option>
            </select>
          </div>
        </EditDrawer>
      )}

      {sendPwFor && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1A1A]/60 flex items-center justify-center px-4"
          onClick={() => setSendPwFor(null)}
          data-testid="send-pw-modal"
        >
          <div
            className="bg-[#FAFAFA] max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="overline text-[#8B6D5E]">— Enviar contraseñas</p>
                <h3 className="font-serif text-2xl mt-1">{sendPwFor.name}</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">{sendPwFor.email}</p>
              </div>
              <button
                onClick={() => setSendPwFor(null)}
                className="text-2xl leading-none text-[#1A1A1A]"
                aria-label="Cerrar"
                data-testid="send-pw-close"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-[#5C5C5C] mb-4">
              Selecciona las unidades cuyas contraseñas quieres enviar por email. Puedes
              enviar de una en una según su avance, o varias a la vez.
            </p>
            <div className="border border-[#E8E1DA] divide-y divide-[#E8E1DA] max-h-80 overflow-y-auto mb-5" data-testid="send-pw-list">
              {unitsMeta.map((u) => {
                const already = (sendPwFor.unlocked_units || []).includes(u.number);
                const checked = sendPwUnits.has(u.number);
                return (
                  <label
                    key={u.number}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F7F0E8]/40 ${
                      checked ? "bg-[#F7F0E8]" : ""
                    }`}
                    data-testid={`send-pw-unit-${u.number}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSendUnit(u.number)}
                      className="accent-[#1A1A1A]"
                    />
                    <span className="overline text-[#8B6D5E] w-8 flex-none">
                      {String(u.number).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-[#1A1A1A] truncate">
                      {u.title}
                    </span>
                    {already && (
                      <span className="overline text-[10px] text-[#3C6348]">Abierta</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="overline text-[#5C5C5C]">
                {sendPwUnits.size} seleccionadas
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSendPwFor(null)}
                  className="btn-outline text-[11px]"
                  data-testid="send-pw-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={sendPasswords}
                  disabled={sendPwLoading || sendPwUnits.size === 0}
                  className="btn-solid text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="send-pw-submit"
                >
                  {sendPwLoading ? "Enviando…" : "Enviar por email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
